import UserProfile from '@zeus/core/UserProfile';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import DocumentTitle from '_/hooks/DocumentTitle';
import {
  useOrgExtractors,
  useFetchMoreOrgExtractors,
  useFetchExtractorAssets,
  useExtractorSources,
  useExtractorVersionsForExtractor,
  useFetchMoreExtractorSources,
  useFetchMoreExtractorVersions,
} from '_/hooks/useExtractors';
import { useUpdateSource } from '_/hooks/useSources';
import useSystemAccess from '_/hooks/useSystemAccess';
import { ISource } from '_/types/sources/source.types';
import { formatDateTime } from '_/utils/dateUtils';
import { compareEditorSort, compareSort } from '_/utils/formatUtils';
import { formatRedashExtractorDashboardUrl } from '_/utils/urlUtils';
import { SUPER_ADMIN_ALLOWED } from '_/utils/userManagement';
import { Button, Spin, Table, Tabs, Tooltip } from 'antd';
import type { TabsProps } from 'antd';
import type { ColumnType } from 'antd/lib/table';
import React, { useState, ReactElement, useMemo, useCallback, useEffect } from 'react';

import ExtractorSourcesDrilldownTable from './components/ExtractorSourcesDrilldownTable';
import ExtractorVersionsDrilldownTable from './components/ExtractorVersionsDrilldownTable';

interface Extractor {
  id: string;
  name: string;
  addedAt: string;
  editor?: any;
  proxyType?: string;
  iso3Country?: string;
}

function OrganizationExtractors(): ReactElement {
  const { selectedOrganization, selectedOrgSlug } = useOrganization();
  const { setBreadcrumbs } = useTopHeader();
  const [selectedExtractorRows, setSelectedExtractorRows] = useState<string[]>([]);
  const hasSystemAccess = useSystemAccess(SUPER_ADMIN_ALLOWED);

  // Set up breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Extractors' },
    ]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs, selectedOrganization, selectedOrgSlug]);

  // Fetch organization extractors
  const { data: extractorsData, isLoading } = useOrgExtractors(selectedOrganization?.id);
  const extractors = extractorsData?.items || [];
  const extractorsCursor = extractorsData?.cursor;

  // Mutations
  const { mutate: fetchMoreExtractors } = useFetchMoreOrgExtractors(selectedOrganization?.id);
  const { mutate: fetchExtractorAssets } = useFetchExtractorAssets();
  const { mutate: updateSourceMutation } = useUpdateSource();

  // Get the selected extractor's data
  const selectedExtractorId = selectedExtractorRows[0];
  const { data: sourcesData } = useExtractorSources(selectedExtractorId);
  const { data: versionsData } = useExtractorVersionsForExtractor(selectedExtractorId);

  // Fetch more mutations for drilldown tables
  const { mutate: fetchMoreSources } = useFetchMoreExtractorSources(selectedExtractorId);
  const { mutate: fetchMoreVersions } = useFetchMoreExtractorVersions(selectedExtractorId);

  // Create lookup objects for nested data (matching Redux structure)
  const extractorSources = useMemo(() => {
    if (!selectedExtractorId || !sourcesData) return {};
    return { [selectedExtractorId]: sourcesData.items };
  }, [selectedExtractorId, sourcesData]);

  const extractorVersions = useMemo(() => {
    if (!selectedExtractorId || !versionsData) return {};
    return { [selectedExtractorId]: versionsData.items };
  }, [selectedExtractorId, versionsData]);

  const columnDefs: ColumnType<Extractor>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
      sorter: compareSort('name'),
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'Added at',
      dataIndex: 'addedAt',
      render: (record: string) => formatDateTime(record),
      sorter: compareSort('addedAt'),
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'Last Editor',
      dataIndex: 'lastEditor',
      render: (_: any, extractor: Extractor) => {
        return <UserProfile tableMode showDetails user={extractor?.editor} />;
      },
      sorter: compareEditorSort,
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'Proxy Type',
      dataIndex: 'proxyType',
      sorter: compareSort('proxyType'),
      sortDirections: ['ascend', 'descend'] as const,
    },
    {
      title: 'ISO Country',
      dataIndex: 'iso3Country',
      sorter: compareSort('iso3Country'),
      sortDirections: ['ascend', 'descend'] as const,
    },
    ...(hasSystemAccess
      ? [
          {
            title: 'Redash',
            width: 100,
            render: (_: any, extractor: Extractor) => (
              <a href={formatRedashExtractorDashboardUrl(extractor.id)} target="_blank" rel="noopener noreferrer">
                Performance
              </a>
            ),
          },
        ]
      : []),
  ];

  const handleUpdateSourceExtractorVersion = useCallback(
    async (source: ISource, extractorVersionId: string) => {
      updateSourceMutation({
        sourceId: source.id,
        updates: { extractorVersionId: extractorVersionId },
      });

      fetchExtractorAssets(source.extractorId);
    },
    [updateSourceMutation, fetchExtractorAssets],
  );

  const expandedRowRender = useCallback(
    (record: Extractor) => {
      const latestVersion = extractorVersions && extractorVersions[record.id] ? extractorVersions[record.id][0] : undefined;

      const expandedRowContainerStyle = {
        maxHeight: 500,
        minHeight: 150,
        overflow: 'hidden',
      };

      const nestedTableContainerStyle = {
        width: '100%',
      };

      const tabItems: TabsProps['items'] = [
        {
          key: '1',
          label: 'Versions',
          children: (
            <div style={nestedTableContainerStyle}>
              <ExtractorVersionsDrilldownTable
                extractorId={record.id}
                extractorVersions={extractorVersions ? extractorVersions[record.id] : []}
                loadMoreExtractorVersions={() => {
                  if (versionsData?.cursor) {
                    fetchMoreVersions(versionsData.cursor);
                  }
                }}
              />
            </div>
          ),
        },
        {
          key: '2',
          label: 'Sources',
          children: (
            <div style={nestedTableContainerStyle}>
              <ExtractorSourcesDrilldownTable
                extractorId={record.id}
                extractorSources={extractorSources ? extractorSources[record.id] : []}
                handleUpdateSourceExtractorVersion={handleUpdateSourceExtractorVersion}
                latestExtractorVersion={latestVersion?.id}
                loadMoreExtractorSources={() => {
                  if (sourcesData?.cursor) {
                    fetchMoreSources(sourcesData.cursor);
                  }
                }}
              />
            </div>
          ),
        },
      ];

      return (
        <div style={expandedRowContainerStyle}>
          <Tabs animated={false} items={tabItems} />
        </div>
      );
    },
    [
      extractorVersions,
      extractorSources,
      handleUpdateSourceExtractorVersion,
      versionsData,
      sourcesData,
      fetchMoreVersions,
      fetchMoreSources,
    ],
  );

  const handleExpandRow = useCallback(
    (expanded: boolean, record: Extractor) => {
      if (expanded) {
        fetchExtractorAssets(record.id);
        setSelectedExtractorRows([record.id]);
      } else {
        setSelectedExtractorRows([]);
      }
    },
    [fetchExtractorAssets],
  );

  DocumentTitle('Extractors');

  const flexEndStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 15,
  };

  return (
    <>
      <Table
        showHeader
        expandRowByClick
        columns={columnDefs}
        dataSource={extractors}
        onExpand={handleExpandRow}
        expandedRowRender={expandedRowRender}
        expandedRowKeys={selectedExtractorRows}
        indentSize={0}
        loading={isLoading}
        rowKey={(record) => record.id}
        pagination={false}
      />
      <div style={flexEndStyle}>
        {!!extractorsCursor && (
          <Button
            onClick={() => {
              if (extractorsCursor) {
                fetchMoreExtractors(extractorsCursor);
              }
            }}
            disabled={isLoading}
          >
            {isLoading ? <Spin size="small" /> : 'Load More'}
          </Button>
        )}
      </div>
    </>
  );
}

export default OrganizationExtractors;
